import { Box, SxProps, TextField } from '@mui/material';
import { Typography, __SWEEP_DESIGN_TYPOGRAPHY_STYLES___ } from '@sweep-io/sweep-design';
import { SweepTypographyVariants } from '@sweep-io/sweep-design/dist/components/Typography/types';
import { forwardRef, useRef, useState } from 'react';
import { ClickableTag } from '../documentation/dependencies/annotations/tags/ClickableTag';
import useResizeObserver from '../../hooks/useResizeObserver';
import { CenteredCircularProgress } from './CenteredCircularProgress';

export interface TagItem {
  value: string;
  label: string;
  startIconName?: string;
  color?: string;
  onDelete?: (value: string) => void;
  onClick?: (value: string) => void;
  labelTooltip?: string;
  deleteTooltip?: string;
  isLoader?: boolean;
}

export interface TextFieldWithTagsProps {
  onChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  tags: TagItem[];
  onAddValue?: (value: string) => any;
  isValueValid?: (value: string) => boolean;
  placeholder?: string;
  error?: string;
  readonly?: boolean;
  startText?: string;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  autoFocus?: boolean;
  value?: string;
  sx?: SxProps;
  hideDeleteButton?: boolean;
  typographyVariant?: SweepTypographyVariants;
  removeBorders?: boolean;
}

export const TextFieldWithTags = forwardRef<HTMLDivElement, TextFieldWithTagsProps>(
  (
    {
      onChange,
      tags,
      onAddValue,
      isValueValid,
      error,
      placeholder: inputPlaceholder = '',
      readonly,
      startText,
      autoFocus,
      onClick,
      value,
      sx,
      hideDeleteButton,
      typographyVariant = 'body',
      removeBorders,
    },
    ref,
  ) => {
    const handleKeyDown = (evt: any) => {
      if (!onAddValue) return;
      if (['Enter', 'Tab', ','].includes(evt.key)) {
        evt.preventDefault();
        const value = evt.target.value.trim();
        if (value && (!isValueValid || isValueValid(value))) {
          evt.target.value = '';
          onAddValue(value);
        }
      }
    };
    const [inputWidth, setInputWidth] = useState<number | undefined>();

    const inputRef = useRef(null);

    useResizeObserver({
      ref: inputRef,
      onResize: (size) => {
        setInputWidth(size.width);
      },
    });

    const renderTags = () => {
      if (tags.length === 0 && !startText) return null;
      return (
        <Box
          sx={{
            gap: '8px',
            display: 'flex',
            flexWrap: 'wrap',
            '.MuiChip-label': {
              maxWidth: inputWidth ? inputWidth - 40 : 'auto',
            },
          }}
          className="tf-w-tags-tags"
        >
          {startText && (
            <Box marginRight="12px">
              <Typography variant="body" key="Typography">
                {startText}
              </Typography>
            </Box>
          )}

          {tags.map((tag) =>
            tag.isLoader ? (
              <CenteredCircularProgress key={tag.value + 'loader'} size={20} />
            ) : (
              <ClickableTag
                key={tag.value}
                onDelete={
                  readonly || hideDeleteButton
                    ? undefined
                    : () => {
                        tag.onDelete && tag.onDelete(tag.value);
                      }
                }
                onClick={
                  readonly || tag.onClick === undefined
                    ? undefined
                    : () => {
                        tag.onClick && tag.onClick(tag.value);
                      }
                }
                label={tag.label}
                startIconName={tag.startIconName}
                color={tag.color}
                labelTooltip={tag.labelTooltip}
                deleteTooltip={tag.deleteTooltip}
              />
            ),
          )}
        </Box>
      );
    };

    return (
      <TextField
        data-testid="text-field-with-tags"
        disabled={readonly}
        ref={ref}
        onBlur={handleKeyDown}
        onChange={onChange}
        onKeyDown={handleKeyDown}
        error={Boolean(error)}
        helperText={error}
        onClick={onClick}
        autoFocus={autoFocus}
        value={value}
        InputProps={{
          ref: inputRef,
          placeholder: inputPlaceholder,
          sx: {
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            rowGap: '8px',
            paddingTop: '10px',
            paddingBottom: '10px',

            '& input': {
              minWidth: '30px',
              width: 'auto',
              paddingTop: '0',
              paddingBottom: 0,
              alignSelf: 'center',
              fontSize: __SWEEP_DESIGN_TYPOGRAPHY_STYLES___[typographyVariant]?.fontSize,
              fontWeight: __SWEEP_DESIGN_TYPOGRAPHY_STYLES___[typographyVariant]?.fontWeight,
            },

            '& .MuiOutlinedInput-input::placeholder': {
              fontSize: __SWEEP_DESIGN_TYPOGRAPHY_STYLES___[typographyVariant]?.fontSize,
              fontWeight: __SWEEP_DESIGN_TYPOGRAPHY_STYLES___[typographyVariant]?.fontWeight,
            },
          },
          disabled: readonly,
          startAdornment: renderTags(),
        }}
        sx={{
          '.MuiOutlinedInput-notchedOutline': {
            border: removeBorders ? 'none' : 'auto',
          },
          ...sx,
        }}
      />
    );
  },
);
